/**
 * "Circuit Breaker" — original composition for "Form Foundry".
 * 140 BPM, E minor, 32-beat loop: driving kick, offbeat hats, Em–C–G–D bass,
 * urgent arpeggio, darker pad. Same synth engine as "Neon Circuit".
 */
import type { Track, NoteEvent } from '../../audio/track';

const E1 = 28,
  C2 = 36,
  G1 = 31,
  D2 = 38;
const E3 = 52,
  G3 = 55,
  B3 = 59,
  E4 = 64,
  C4 = 60,
  G4 = 67,
  D4 = 62,
  FS4 = 66,
  A3 = 57,
  B2 = 47;

function beats(n: number, step: number, offset = 0): number[] {
  const out: number[] = [];
  for (let b = offset; b < n; b += step) out.push(b);
  return out;
}

function bassBar(startBeat: number, root: number): NoteEvent[] {
  const out: NoteEvent[] = [];
  const pattern = [0, 12, 0, 7, 0, 12, 7, 12];
  for (let i = 0; i < 16; i++) {
    out.push({
      beat: startBeat + i * 0.5,
      midi: root + (pattern[i % 8] ?? 0),
      len: 0.4,
      vel: i % 4 === 0 ? 0.95 : 0.65,
    });
  }
  return out;
}

function arp(startBeat: number, notes: number[], beatsLen: number): NoteEvent[] {
  const out: NoteEvent[] = [];
  const steps = beatsLen * 4;
  for (let i = 0; i < steps; i++) {
    out.push({
      beat: startBeat + i * 0.25,
      midi: notes[i % notes.length] ?? 64,
      len: 0.2,
      vel: 0.45 + (i % 8 === 0 ? 0.25 : 0),
    });
  }
  return out;
}

const CHORDS: Array<{ root: number; triad: number[]; pad: number[] }> = [
  { root: E1, triad: [E3, G3, B3, E4], pad: [E3, G3, B3] }, // Em
  { root: C2, triad: [C4, E4, G4, E4], pad: [C4 - 12, E3, G3] }, // C
  { root: G1, triad: [G3, B3, D4, G4], pad: [G3, B3, D4] }, // G
  { root: D2, triad: [D4, FS4, A3 + 12, FS4], pad: [D4 - 12, FS4 - 12, A3] }, // D
];

const LOOP_BEATS = 32;
const bass: NoteEvent[] = [];
const lead: NoteEvent[] = [];
const pad: NoteEvent[] = [];
CHORDS.forEach((ch, i) => {
  const start = i * 8;
  bass.push(...bassBar(start, ch.root));
  lead.push(...arp(start, ch.triad, 8));
  pad.push(...ch.pad.map((midi) => ({ beat: start, midi, len: 7.5, vel: 0.3 })));
});
// Low drone anchors the loop.
pad.push({ beat: 0, midi: B2, len: 31, vel: 0.12 });

export const CIRCUIT_BREAKER: Track = {
  id: 'circuit-breaker',
  title: 'Circuit Breaker',
  bpm: 140,
  loopBeats: LOOP_BEATS,
  kick: beats(LOOP_BEATS, 1),
  hat: beats(LOOP_BEATS, 0.5, 0.5).filter((b) => b % 1 !== 0),
  snare: beats(LOOP_BEATS, 2, 1),
  bass,
  lead,
  pad,
};
